import React, { useEffect, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Sparkles, Activity, Eye, Target, Lightbulb, Loader2, ArrowRight,
} from "lucide-react";
import CockpitPreviewModal from "@/components/CockpitPreviewModal";
import CockpitSection from "@/components/CockpitSection";
import CockpitHeatmap from "@/components/CockpitHeatmap";
import CockpitTimeline from "@/components/CockpitTimeline";
import CockpitCoachVoice from "@/components/CockpitCoachVoice";
import CockpitFreeBanner from "@/components/CockpitFreeBanner";
// V1.D (Launch-Consolidation, 30.08.2026): ProRoadmapAusblick entfernt.
// Grund: Sichtbare Roadmap-Sektion konfligiert mit V1-Scope-Discipline
// ("lieber ehrlich als verkaufen"). Post-Launch wieder aktivieren.
// import ProRoadmapAusblick from "@/components/ProRoadmapAusblick";
import AnliegenHistory from "@/components/AnliegenHistory";
import CreditCounter from "@/components/CreditCounter";
import FreeFokustypBibliothek from "@/components/FreeFokustypBibliothek";
import WieGehtEsWeiter from "@/components/WieGehtEsWeiter";
import MultiKursBibliothek from "@/components/MultiKursBibliothek";
import OnboardingNudge from "@/components/OnboardingNudge";
import ReEntryModal from "@/components/ReEntryModal";
import ReadOnlyBanner from "@/components/ReadOnlyBanner";
import StreakCounter from "@/components/StreakCounter";
import ModeSwitcher from "@/components/ModeSwitcher";
import QuickModeBanner from "@/components/QuickModeBanner";
import DetailMode from "@/components/DetailMode";
import TriggerBanner from "@/components/TriggerBanner";
import InlineFeedbackTrigger from "@/components/InlineFeedbackTrigger";
import AvatarTag from "@/components/AvatarTag";
import CockpitEmptyState from "@/components/CockpitEmptyState";
import CoachingAuftragModal from "@/components/CoachingAuftragModal";
import PionierBadge from "@/components/PionierBadge";
import PionierBanner from "@/components/dashboard/PionierBanner";
// Sprint M5.40 (28.07.2026) — Delta 1: Teaser-Sektion fuer Free-Tier-User.
// Sprint M5.37 (29.07.2026) — MehrEntdeckenTeaser komplett entfernt (Block B):
// Free zeigt nur Live-Pro-Features, Pro bekommt stattdessen ProRoadmapAusblick.
// Sprint M5.23 (29.07.2026) — Pre-Ablefy Upgrade-Modal.
import UpgradeModal from "@/components/UpgradeModal";
// Sprint v1.6 Stufe 4A/4B/4C (Mi 02.09.2026) — Wirkung-gestalten-Section
// (Türöffner + Zugangscode + Kommunikationspaket). Metaphern-Hierarchie v1.4.
import TueroeffnerKarte from "@/components/TueroeffnerKarte";
import Zugangscode from "@/components/Zugangscode";
import Kommunikationspaket from "@/components/Kommunikationspaket";
// Sprint v1.6 Stufe 4D (Do 03.09.2026) — StressmusterGrid in Phase 1 „Mein Muster".
import StressmusterGrid from "@/components/StressmusterGrid";
// Sprint v1.7 Phase 4 (Do 03.09.2026) — PhaseAnchor-Wrapper mit Step-Nummerierung.
import PhaseAnchor from "@/components/PhaseAnchor";
import { COPY } from "@/constants/copy";
import { isPremium } from "@/lib/licensing";

const MODE_STORAGE_KEY = "f4s_cockpit_mode";

// Sprint 8.A.2 — Burner-Cockpit: Heatmap + animierte Timeline + Coach-Voice + Onboarding-Nudge.
// 4 Kacheln auf einer Seite: Activity + Heatmap + Timeline + Hot Tips.
// Free-User: 3 Heatmap-Zellen sichtbar, 13 locked; Hot Tips 3/8 sichtbar; subtiler Free-Banner.

const NAMES = { eule: "Eule", biene: "Biene", katze: "Katze", hund: "Hund" };

// Animierter Counter
function AnimatedCount({ value, testId }) {
  const [n, setN] = useState(0);
  useEffect(() => {
    const target = Number(value) || 0;
    if (target <= 0) return;
    const steps = Math.min(target, 30);
    const dur = 700;
    const stepDelay = dur / steps;
    let i = 0;
    const timer = setInterval(() => {
      i += 1;
      const progress = i / steps;
      setN(Math.round(target * progress));
      if (i >= steps) clearInterval(timer);
    }, stepDelay);
    return () => clearInterval(timer);
  }, [value]);
  return <span data-testid={testId}>{n}</span>;
}

export default function Cockpit() {
  const navigate = useNavigate();
  const { user: authUser } = useAuth();
  const [burner, setBurner] = useState(null);
  const [previews, setPreviews] = useState(null);
  const [savedBruecken, setSavedBruecken] = useState([]);
  const [retention, setRetention] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  // Sprint M5.23 (29.07.2026) — Pre-Ablefy Upgrade-Modal (Free → Pro Warteliste)
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  // Sprint M5.42 Block B (31.07.2026) — Teil D Mini-Fix:
  // CoachingAuftragModal oeffnet nur per expliziten User-Klick.
  const [coachingAuftragOpen, setCoachingAuftragOpen] = useState(false);
  const [coachingAuftragHas, setCoachingAuftragHas] = useState(null); // null=unknown, true=exists, false=none
  // Sprint 8.B — Mode-State: quick | insight | detail
  const [mode, setMode] = useState(() => {
    try {
      const saved = localStorage.getItem(MODE_STORAGE_KEY);
      if (saved === "insight" || saved === "detail") return saved;
    } catch (_) { /* noop */ }
    return "quick";
  });

  const handleModeChange = (next) => {
    setMode(next);
    try {
      if (next === "insight" || next === "detail") {
        localStorage.setItem(MODE_STORAGE_KEY, next);
      }
    } catch (_) { /* noop */ }
  };

  const switchToInsight = () => handleModeChange("insight");

  // Sprint 8.F — Inactivity-Trigger nach 30s ohne Mausbewegung/Klick
  const [showInactivityTrigger, setShowInactivityTrigger] = useState(false);
  useEffect(() => {
    let t;
    const reset = () => {
      if (t) clearTimeout(t);
      t = setTimeout(() => setShowInactivityTrigger(true), 30000);
    };
    const events = ["mousemove", "keydown", "click", "scroll", "touchstart"];
    events.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    reset();
    return () => {
      if (t) clearTimeout(t);
      events.forEach((e) => window.removeEventListener(e, reset));
    };
  }, []);

  // Sprint 8.F — Post-Flexpfad-Trigger 5s nach Cockpit-Aufruf, wenn sessionStorage-Flag gesetzt
  const [showPostBrueckeTrigger, setShowPostBrueckeTrigger] = useState(false);
  const [recentBrueckeId, setRecentBrueckeId] = useState(null);
  useEffect(() => {
    let recent = null;
    try {
      recent = sessionStorage.getItem("f4s_recent_bruecke_id");
    } catch (_) { /* noop */ }
    if (recent) {
      setRecentBrueckeId(recent);
      const t = setTimeout(() => setShowPostBrueckeTrigger(true), 5000);
      return () => clearTimeout(t);
    }
    return undefined;
  }, []);

  useEffect(() => {
    Promise.all([
      api.get("/cockpit/burner").then((r) => r.data).catch(() => null),
      api.get("/cockpit/previews").then((r) => r.data).catch(() => null),
      api.get("/anliegen/saved?limit=5").then((r) => r.data?.items || []).catch(() => []),
      api.get("/account/retention").then((r) => r.data?.state || null).catch(() => null),
    ]).then(([b, p, s, ret]) => {
      setBurner(b);
      setPreviews(p);
      setSavedBruecken(s);
      setRetention(ret);
      setLoading(false);
    });
    // Sprint M5.42 Block B (31.07.2026) — Teil D:
    // Existenz-Check ist entkoppelt vom Modal, damit der Trigger-Button
    // nur erscheint, wenn noch kein Coaching-Auftrag existiert.
    api
      .get("/onboarding/coaching-auftrag")
      .then((r) => setCoachingAuftragHas(!!r?.data?.auftrag))
      .catch(() => setCoachingAuftragHas(null));
  }, []);

  if (loading) {
    return (
      <DashboardLayout>
        {() => (
          <div className="flex justify-center py-24">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
          </div>
        )}
      </DashboardLayout>
    );
  }

  // Sprint 12+M5.13 (19.07.2026) — Architektur-Schutz: Pioniere gehoeren
  // auf /dashboard/pionier, NIE auf /cockpit (Free-User-Cockpit mit
  // Upgrade-Optionen). Wenn ein Pionier hier landet (z. B. via Bookmark
  // oder buggem Redirect), zurueck auf sein Cockpit.
  //
  // Sprint M5.pionier-switch (28.07.2026) — Env-Flag PIONIER_ACTIVE steuert,
  // ob dieser Redirect noch feuert. Default (false) migriert alle Pioniere
  // in die Standard-Free-UI. Pionier-Badge bleibt rein visuell erhalten.
  if (
    authUser &&
    typeof authUser === "object" &&
    authUser.is_pionier &&
    authUser.pionier_active === true
  ) {
    return <Navigate to="/dashboard/pionier" replace />;
  }

  const openModal = () => setModalOpen(true);

  return (
    <DashboardLayout>
      {({ data }) => {
        const user = data.user;
        // Single source of truth — siehe /app/frontend/src/lib/licensing.js.
        const isPro = isPremium(user);
        const firstName = (user?.first_name || "").trim();
        const fokustyp = burner?.primary_fokustyp || previews?.primary_fokustyp;
        // Bug-Fix Mini-Hotfix 29.06.2026 — User ohne abgeschlossenen Fokustyp-Test.
        // Beide Feldnamen prüfen (Tech-Debt: `primary_fokustyp` ↔ `fokustyp_primary`).
        const hasFokustyp = Boolean(
          user?.primary_fokustyp || user?.fokustyp_primary || fokustyp,
        );
        const brueckenCount = burner?.bruecken_count ?? savedBruecken.length;
        const musterCount = burner?.muster_count ?? 0;
        const daysActive = burner?.days_since_start ?? 0;
        const coach = burner?.coach_voice || {};

        return (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-24" data-testid="cockpit-page">
            {/* Sprint 12+C (03.07.2026) — Pionier-Banner (nur für User mit pionier-Tag) */}
            <PionierBanner user={user} />
            {/* Sprint 8.E — Read-Only-Banner für cancelled User */}
            <ReadOnlyBanner
              retention={retention}
              onReactivate={() => {
                // Scroll to ReEntryModal would auto-open; instead open via reload retention modal trigger
                try { sessionStorage.removeItem(`f4s_reentry_dismissed_${user?.id || user?._id || ""}`); } catch (_) { /* noop */ }
                window.location.reload();
              }}
            />

            {/* Sprint 8.B — Quick-Mode-Banner (nur Pro, nicht im Read-Only) */}
            {isPro && !retention?.read_only && (
              <QuickModeBanner mode={mode} onSwitchToInsight={switchToInsight} />
            )}

            {/* Sprint 8.C — Conversational Trigger Banner (Pro, nicht Read-Only) */}
            {isPro && !retention?.read_only && <TriggerBanner />}
            {/* ============ HEADER ============ */}
            <header className="mb-8" data-testid="cockpit-header">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <motion.h1
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  className="text-2xl sm:text-3xl font-extrabold tracking-tight text-[var(--fts-text-primary)]"
                >
                  {retention?.read_only ? (
                    <>{COPY.cockpitHeaderH1.readOnly}{firstName ? `, ${firstName}` : ""}</>
                  ) : isPro ? (
                    <>{COPY.cockpitHeaderH1.pro}{firstName ? `, ${firstName}` : ""}.</>
                  ) : (
                    <>{COPY.cockpitHeaderH1.free}{firstName ? `, ${firstName}` : ""}.</>
                  )}
                </motion.h1>

                {/* Sprint 8.D — Avatar-Tag mit Milestone (7/30/90/180/365 Tage). */}
                {!retention?.read_only && daysActive >= 7 && (
                  <AvatarTag daysActive={daysActive} />
                )}

                {/* Sprint 8.B — Mode-Switcher nur für Pro-User sichtbar, nicht im Read-Only */}
                {isPro && !retention?.read_only && (
                  <ModeSwitcher mode={mode} onChange={handleModeChange} />
                )}
              </div>

              {/* Header-Stats Row */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.4, delay: 0.2 }}
                data-testid="cockpit-header-stats"
                className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-sm text-slate-600"
              >
                <span>
                  📊{" "}
                  <strong>
                    <AnimatedCount value={brueckenCount} testId="cockpit-stat-bruecken-count" />
                  </strong>{" "}
                  Flexpfade
                </span>
                {isPro && (
                  <span>
                    🪟{" "}
                    <strong>
                      <AnimatedCount value={musterCount} testId="cockpit-stat-muster-count" />
                    </strong>{" "}
                    Muster
                  </span>
                )}
                <span>
                  💡 <strong>{isPro ? 8 : 3}</strong> Hot Tips
                </span>
                {daysActive > 0 && <span>🗓 {daysActive} Tage dabei</span>}
                {fokustyp && (
                  <span data-testid="cockpit-stat-primaer">
                    🎯 {NAMES[fokustyp] || fokustyp} Primärtyp
                  </span>
                )}
                {/* Sprint M5.23 (29.07.2026) — Plan-Badge (v8-Naming). */}
                <PlanBadge user={user} onUpgradeClick={() => setUpgradeOpen(true)} />
                {/* Sprint 12+ Phase 4 (Pionier-Skeleton) — Badge nur wenn user.is_pionier */}
                <PionierBadge user={user} />
                {/* Sprint M5.42 (30.07.2026) — Block A3: KI-Credit-Counter (Pro-only,
                    Free bekommt 404 vom Backend und Counter versteckt sich). */}
                {isPro && <CreditCounter />}
              </motion.div>

              <CockpitCoachVoice
                variant="header"
                text={coach.header}
                testId="cockpit-coach-header"
              />

              {/* Sprint 8.A.2.5 — Flexpfad-Serie (täglicher Pull-Mechanismus) */}
              {!retention?.read_only && burner?.streak && (
                <StreakCounter
                  streak={burner.streak}
                  isPro={isPro}
                  onUpgradeClick={!isPro ? openModal : undefined}
                />
              )}
            </header>

            {/* Sprint 8.B — Mode-konditionales Rendering: Detail-Mode zeigt volle Bibliothek */}
            {mode === "detail" && isPro && !retention?.read_only ? (
              <DetailMode user={user} burner={burner} previews={previews} isPro={isPro} />
            ) : !hasFokustyp && !retention?.read_only ? (
              /* Bug-Fix Mini-Hotfix 29.06.2026: User ohne Fokustyp-Test bekommen
                 prominenten Empty-State statt 4 leerer Kacheln.
                 Sprint M5.42 Block B (31.07.2026): Pioniere sollen den
                 Vorab-Einblick-Kurs AUCH erreichen koennen, wenn sie noch
                 keinen Fokustyp haben. */
              <>
                {user?.is_pionier && <MultiKursBibliothek isPionier={true} showComingSoon={false} />}
                <CockpitEmptyState />
              </>
            ) : (
            <>
            {/* ============ GRID (Insight-Mode + Quick-Default) ============ */}
            {/* Sprint M5.38 (30.07.2026) — Block C1: 4-Kacheln-Pro-Preview-Grid
                (Activity, Heatmap, Timeline, Hot Tips) ist nicht in Free-Spec
                (F-1..F-9). Nur Pro-User sehen den kompletten Block. Free bekommt
                stattdessen unten die spec-konforme Mehr-entdecken-Sektion. */}
            {isPro && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              {/* === Activity (immer unlocked) === */}
              <CockpitSection
                icon={Activity}
                title="Deine letzten Flexpfade"
                testId="cockpit-tile-activity"
              >
                {savedBruecken.length === 0 ? (
                  <div className="text-sm text-slate-500">
                    Noch keine Flexpfade.{" "}
                    <button
                      type="button"
                      onClick={() => navigate("/dashboard/anliegen")}
                      data-testid="cockpit-build-first-cta"
                      className="text-[#4563BB] font-semibold hover:underline"
                    >
                      Lege deinen ersten an →
                    </button>
                  </div>
                ) : (
                  <ul className="space-y-2" data-testid="cockpit-activity-list">
                    {savedBruecken.slice(0, 4).map((b) => (
                      <li
                        key={b.id}
                        className="text-sm flex items-start gap-2"
                        data-testid={`cockpit-activity-item-${b.id}`}
                      >
                        <span className="text-slate-400 text-xs font-mono pt-0.5 flex-shrink-0">
                          {(b.saved_at || "").slice(5, 10)}
                        </span>
                        <span className="text-slate-700 line-clamp-1">
                          {b.bridge_text || b.situation || "Flexpfad"}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
                <button
                  type="button"
                  onClick={() => navigate("/dashboard/anliegen?tab=saved")}
                  className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-[#4563BB] hover:underline"
                  data-testid="cockpit-all-bruecken-cta"
                >
                  Alle Flexpfade anzeigen <ArrowRight className="w-3 h-3" />
                </button>
                <CockpitCoachVoice text={coach.activity} testId="cockpit-coach-activity" />
              </CockpitSection>

              {/* === Heatmap === */}
              <CockpitSection
                icon={Eye}
                title={isPro ? "Deine Flexpfad-Reise" : "Deine Top-3-Muster"}
                testId="cockpit-tile-muster"
              >
                {brueckenCount > 0 && burner?.heatmap ? (
                  <CockpitHeatmap
                    data={burner.heatmap}
                    isPro={isPro}
                    topMuster={burner.top_muster}
                    onLockedClick={openModal}
                  />
                ) : (
                  <p className="text-sm text-slate-500 italic">
                    Noch keine Flexpfade — die Flexpfad-Reise füllt sich mit jedem gespeicherten Flexpfad.
                  </p>
                )}
                <CockpitCoachVoice text={coach.heatmap} testId="cockpit-coach-heatmap" />
                {!isPro && (
                  <button
                    type="button"
                    onClick={openModal}
                    data-testid="cockpit-tile-muster-unlock"
                    className="mt-3 inline-flex items-center gap-1.5 text-xs font-bold text-[#4563BB] hover:underline"
                  >
                    Alle Werkzeuge freischalten <ArrowRight className="w-3 h-3" />
                  </button>
                )}
              </CockpitSection>

              {/* === Timeline === */}
              <CockpitSection
                icon={Target}
                title="Deine Flexpfad-Reise"
                testId="cockpit-tile-timeline"
              >
                {burner?.timeline ? (
                  <CockpitTimeline data={burner.timeline} />
                ) : (
                  <p className="text-sm text-slate-500 italic">Keine Daten.</p>
                )}
                <CockpitCoachVoice text={coach.timeline} testId="cockpit-coach-timeline" />
              </CockpitSection>

              {/* === Hot Tips === */}
              <CockpitSection
                icon={Lightbulb}
                title={isPro ? "Hot Tips diese Woche" : "Drei Tipps für dich"}
                testId="cockpit-tile-tips"
              >
                <ol className="space-y-2 list-decimal list-inside" data-testid="cockpit-tips-list">
                  {(previews?.hot_tips || []).slice(0, isPro ? 8 : 3).map((t, i) => (
                    <li key={i} className="text-sm text-slate-700">
                      <span className="font-semibold">{t.title}</span>
                      {t.insight && (
                        <p className="ml-5 text-xs text-slate-500 leading-snug mt-0.5">
                          {t.insight}
                        </p>
                      )}
                    </li>
                  ))}
                </ol>
                {!isPro && (previews?.hot_tips || []).length > 3 && (
                  <button
                    type="button"
                    onClick={openModal}
                    data-testid="cockpit-tile-tips-unlock"
                    className="mt-3 inline-flex items-center gap-1.5 text-xs font-bold text-[#4563BB] hover:underline"
                  >
                    Alle 8 Tipps freischalten <ArrowRight className="w-3 h-3" />
                  </button>
                )}
                <CockpitCoachVoice text={coach.tips} testId="cockpit-coach-tips" />
              </CockpitSection>
            </div>
            )}

            {/* Sprint M5.39 (30.07.2026) — Block A2 + A3 + B2:
                Free-Cockpit spec-konform (5 Sektionen):
                  1. Quick-Check (bereits in Quicknav)
                  2. Multi-Kurs-Video-Bibliothek
                  3. Fokustypen-Bibliothek (Voll- vs. Preview)
                  4. „Wie geht es weiter?"
                  5. Plan-Badge (bereits im Header oben)
                Der frueher hier eingesetzte Mehr-entdecken-Block wurde durch
                die neue Spec (Big-Picture statt Feature-Liste) ersetzt. */}
            {!isPro && !retention?.read_only && (
              <>
                <MultiKursBibliothek
                  onOpenCourse={() => navigate("/dashboard/videos")}
                  isPionier={!!user?.is_pionier}
                  showComingSoon={false}
                />

                {/* Sprint v1.7 Phase 4 (Do 03.09.2026) — 3-Phasen-Gruppierung
                    im Cockpit mit permanenter Sidebar-Navigation, Step-Nummerierung
                    ("Schritt X von 3"), Progress-Bar, und "Was kommt als Nächstes"-
                    Teasern (MarketingSecrets.ai-Pattern für User-Orientierung).
                    Selfmanagement-Toolbox NICHT als Top-Level-Punkt (Addon in
                    Trainings/Kurse post-launch). */}

                {/* ============ PHASE 1 — MEIN MUSTER ============ */}
                <PhaseAnchor
                  step={1}
                  id="phase-mein-muster"
                  eyebrow="Mein Muster"
                  title="Wie ticke ich — und was macht mein Muster daraus?"
                  description="Selbsterkenntnis. Erkenne dein Kommunikations-Muster und deine Reaktion unter Druck, bevor sie dich in die Enge treibt."
                >
                  <StressmusterGrid />
                </PhaseAnchor>

                {/* ============ PHASE 2 — ANDERE VERSTEHEN ============ */}
                <PhaseAnchor
                  step={2}
                  id="phase-andere-verstehen"
                  eyebrow="Andere verstehen"
                  title="Was zeigt dir dein Gegenüber?"
                  description="Andere-Kenntnis. Fokustyp-Radar, erste Impulse, Tonalität — hier liest du, wer da vor dir steht."
                >
                  <FreeFokustypBibliothek
                    primaryFokustyp={fokustyp}
                    onUpgradeClick={() => setUpgradeOpen(true)}
                  />
                </PhaseAnchor>

                {/* ============ PHASE 3 — WIRKUNG GESTALTEN ============ */}
                <PhaseAnchor
                  step={3}
                  id="phase-wirkung-gestalten"
                  eyebrow="Wirkung gestalten"
                  title="So öffnest du die Tür."
                  description="Gestaltung. Der Türsteher entscheidet, ob du gehört wirst. Diese drei Bausteine zeigen dir, wie er tickt und was ihn öffnet."
                >
                  <TueroeffnerKarte
                    slug={fokustyp}
                    onAction={() => navigate("/dashboard/anliegen")}
                  />
                  <Zugangscode slug={fokustyp} />
                  <Kommunikationspaket />
                </PhaseAnchor>

                <WieGehtEsWeiter isPro={false} />
              </>
            )}
            {/* Sprint M5.40 (30.07.2026) — Block D1: Auch Pro-User sehen die
                5-Schritt-Value-Ladder, mit anderer Marker-Verteilung (1+2 done,
                3 „Du bist hier", 4+5 offen). */}
            {isPro && !retention?.read_only && (
              <>
                {/* Vorlaunch-Sprint 04.08.2026 — Gate C: Grundlagenbibliothek
                    fuer ALLE Pro-Solo-User (auch ohne Pionier-Status) sichtbar.
                    isPionier={false} → nur Grundlagen-Kurs, KEIN Ruhe-im-Chaos.
                    Ruhe-im-Chaos bleibt strikt hinter separatem is_pionier-Guard. */}
                {!user?.is_pionier && (
                  <MultiKursBibliothek
                    onOpenCourse={() => navigate("/dashboard/videos")}
                    isPionier={false}
                    showComingSoon={false}
                  />
                )}
                {/* Sprint M5.42 Block B (31.07.2026) — Pro-Pioniere sehen
                    „Ruhe im Chaos" ebenfalls als extra Kachel. */}
                {user?.is_pionier && (
                  <MultiKursBibliothek isPionier={true} showComingSoon={false} />
                )}
                <WieGehtEsWeiter isPro={true} />
              </>
            )}
            {coach.cta && !retention?.read_only && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.4 }}
                data-testid="cockpit-cta-band"
                className="mt-8 rounded-2xl p-5 text-white flex items-center gap-4"
                style={{ backgroundColor: "#4563BB" }}
              >
                <Sparkles className="w-5 h-5 flex-shrink-0" />
                <div className="flex-1">
                  <CockpitCoachVoice variant="cta" text={coach.cta} />
                </div>
                {/* Sprint M5.40 (30.07.2026) — Block A1:
                    „Flexpfad anlegen" ist Pro-only. Free-User sehen stattdessen
                    einen dezenten Upgrade-Hinweis oder gar kein CTA. */}
                {/* Sprint M5.47 Hotfix (02.08.2026): Pionier ist KEIN
                    Upgrade-Kandidat mehr → Header-CTA fuehrt fuer Pioniere
                    (wie Pro) zum Flexpfad-Anlegen statt zu /pricing. */}
                {(isPro || user?.is_pionier) ? (
                  <button
                    type="button"
                    onClick={() => navigate("/dashboard/anliegen")}
                    data-testid="cockpit-cta-action"
                    className="rounded-xl bg-white text-[#4563BB] font-bold px-4 py-2 text-sm whitespace-nowrap hover:bg-slate-100"
                  >
                    Neuen Flexpfad anlegen
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => navigate("/pricing")}
                    data-testid="cockpit-cta-action-free"
                    className="rounded-xl bg-white text-[#4563BB] font-bold px-4 py-2 text-sm whitespace-nowrap hover:bg-slate-100"
                  >
                    Pro Solo starten
                  </button>
                )}
              </motion.div>
            )}

            {/* Hotfix v14f — Cockpit-Hub: Schnellzugriff zu allen Tools (Querverweise).
                Sichtbar für alle Tiers (auch Read-Only), navigation ist immer erlaubt.
                Sprint M5.35 (29.07.2026) — Sidebar-Reduktion: Free-Tier sieht nur 4
                aktive Live-Bereiche (Cockpit, M-11-Analyse, History, Library).
                Pro-User sehen zusaetzlich Steckbriefe / 3-Etappen / Fokustyp-Test / Plan / Feedback.
            */}
            </>
            )}

            {/* Sprint M5.35 (29.07.2026) — Quicknav aus Fokustyp-Gate herausgezogen.
                Neu-User (Launch-Tag) sehen die 4 Kern-Kacheln SOFORT, nicht erst nach Fokustyp-Test. */}
            {!retention?.read_only && (
              <motion.section
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.5 }}
                data-testid="cockpit-quicknav"
                className="mt-10"
              >
                <h2 className="text-sm font-bold uppercase tracking-wider text-slate-500 mb-3">
                  Schnellzugriff
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                  {/* Sprint M5.40/M5.42 — QuickTiles tier-gegated + Sidebar-Reduktion
                      (Block A8). Pro-Kern-Bereiche: Anliegen anlegen (CTA),
                      M-11, History, Library, Videos, Begegnungs-Dynamik,
                      Empfehlungen. Steckbriefe + 3-Etappen entfernt (via
                      Anliegen-Flow erreichbar). Utility (Mein Plan, PowerPaket,
                      Feedback) als Sub-Items. */}
                  {isPro && (
                    <CockpitQuickTile to="/dashboard/anliegen" icon="🎯" label="Flexpfad anlegen" testid="quicknav-anliegen" />
                  )}
                  {!isPro && (
                    <CockpitQuickTile to="/dashboard/test" icon="🎯" label="Quick-Check (5 Fragen)" testid="quicknav-quickcheck" />
                  )}
                  {isPro && (
                    <CockpitQuickTile to="/dashboard/pro-analyse" icon="🧠" label="M-11 Analyse" testid="quicknav-pro-analyse" />
                  )}
                  {isPro && (
                    <CockpitQuickTile to="/dashboard/anliegen?tab=saved" icon="📚" label="History" testid="quicknav-history" />
                  )}
                  <CockpitQuickTile to="/dashboard/heart-piece-v3" icon="♥" label="Library" testid="quicknav-heartpiece-v3" />
                  <CockpitQuickTile to="/dashboard/videos" icon="🎬" label="Video-Bibliothek" testid="quicknav-videos" />
                  {isPro && (
                    <>
                      {/* Sprint M5.42 (30.07.2026) — Block A1:
                          Begegnungs-Dynamik als eigene Route/Kachel (Pro-only). */}
                      <CockpitQuickTile to="/dashboard/begegnungs-dynamik" icon="🚪" label="Begegnungs-Dynamik" testid="quicknav-begegnungs-dynamik" />
                      <CockpitQuickTile to="/dashboard/recommendations" icon="✨" label="Empfehlungen" testid="quicknav-recommendations" />
                      <CockpitQuickTile to="/dashboard/abo" icon="💳" label="Mein Plan" testid="quicknav-abo" />
                      <CockpitQuickTile to="/mitgestalten" icon="💬" label="Feedback" testid="quicknav-feedback" />
                    </>
                  )}
                  {user?.powerpaket_lifetime && (
                    <CockpitQuickTile to="/dashboard/powerpaket" icon="🧰" label="PowerPaket" testid="quicknav-powerpaket" />
                  )}
                  {!isPro && (
                    <CockpitQuickTile to="/pricing" icon="💳" label="Preise" testid="quicknav-pricing" />
                  )}
                </div>
              </motion.section>
            )}

            {/* V1.D (Launch-Consolidation, 30.08.2026) — Roadmap-Ausblick
                komplett ausgeblendet fuer ALLE Tiers (auch Pro).
                Grund: Scope-Discipline & „lieber ehrlich als verkaufen". */}
            {/* {isPro && !retention?.read_only && <ProRoadmapAusblick />} */}
            {/* Sprint M5.42 (30.07.2026) — Block A7: History mit FT+Datum-Filter,
                nur für Pro (Free hat kein Flexpfad-Anlegen). */}
            {isPro && !retention?.read_only && (
              <AnliegenHistory limit={100} showFilters={true} />
            )}

            {/* Sprint M5.37 (29.07.2026) — Block B: MehrEntdecken für Free entfernt.
                Free zeigt nur Live-Pro-Features (Todd Boardroom-Regel).
                Pro-Solo bekommt stattdessen den Roadmap-Ausblick weiter unten. */}

            {/* Sprint M5.35 (29.07.2026) — PowerPaket-Bundle-Hinweis fuer Free-User,
                nur wenn Launch-Fenster aktiv. Pro-Solo-User sehen einen anderen
                Upsell-Weg (One-Click-Upsell in DashboardAbo). */}
            {!isPro && !retention?.read_only && <FreePowerPaketHint />}

            {/* ============ Modals & Free-Banner ============ */}
            <CockpitPreviewModal open={modalOpen} onClose={() => setModalOpen(false)} />
            {/* Sprint M5.23 (29.07.2026) — Pre-Ablefy Upgrade-Modal (Warteliste). */}
            <UpgradeModal
              open={upgradeOpen}
              onClose={() => setUpgradeOpen(false)}
              email={user?.email || ""}
            />
            {/* Sprint M5.47 Hotfix (02.08.2026): Pioniere sehen keinen
                Pro-Solo-Upsell-Banner. Sie sind kein Upgrade-Kandidat. */}
            {!isPro && !retention?.read_only && !user?.is_pionier && <CockpitFreeBanner onCta={openModal} />}
            {!retention?.read_only && <OnboardingNudge />}
            {/* Sprint 11.6 Block 2 (Task 1) — CoachingAuftragModal.
                Sprint M5.42 Block B (31.07.2026) — Teil D Mini-Fix:
                Kein Auto-Open mehr. Ein dedizierter Trigger-Button erscheint
                nur, wenn User noch KEINEN coaching_auftrag hat. Klick öffnet
                das Modal (controlled via `open`-Prop). */}
            {!retention?.read_only &&
              (user?.primary_fokustyp || user?.fokustyp_primary) &&
              coachingAuftragHas === false && (
                <div
                  className="mt-6 rounded-2xl border border-dashed border-[#4563BB]/40 bg-[#4563BB]/5 p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-3"
                  data-testid="coaching-auftrag-trigger-card"
                >
                  <div className="flex-1">
                    <p className="text-sm font-bold text-slate-900">
                      Möchtest du deinen Coaching-Auftrag formulieren?
                    </p>
                    <p className="text-xs text-slate-600 mt-1">
                      4 Fragen · ~2 Minuten. Damit wir wissen, wo dein Fokus liegt.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setCoachingAuftragOpen(true)}
                    data-testid="coaching-auftrag-trigger-btn"
                    className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-[#4563BB] hover:bg-[#3a54a2] px-4 py-2 text-sm font-semibold text-white"
                  >
                    Jetzt starten
                  </button>
                </div>
              )}
            {!retention?.read_only &&
              (user?.primary_fokustyp || user?.fokustyp_primary) && (
                <CoachingAuftragModal
                  open={coachingAuftragOpen}
                  onClose={() => setCoachingAuftragOpen(false)}
                  onSaved={() => {
                    setCoachingAuftragHas(true);
                    setCoachingAuftragOpen(false);
                  }}
                />
              )}
            {/* Sprint 8.E — Re-Entry-Modal für cancelled User */}
            <ReEntryModal user={user} />
            {/* Sprint 8.F — Inline-Inactivity-Trigger */}
            <InlineFeedbackTrigger
              variant="inactivity"
              show={showInactivityTrigger}
              onShown={() => setShowInactivityTrigger(false)}
              onDismiss={() => setShowInactivityTrigger(false)}
              isReadOnly={!!retention?.read_only}
              context={{ url: window.location.pathname }}
            />
            {/* Sprint 8.F — Post-Flexpfad-Trigger (5s nach Aufruf) */}
            <InlineFeedbackTrigger
              variant="post_bruecke"
              show={showPostBrueckeTrigger}
              onShown={() => {
                setShowPostBrueckeTrigger(false);
                try { sessionStorage.removeItem("f4s_recent_bruecke_id"); } catch (_) { /* noop */ }
              }}
              onDismiss={() => setShowPostBrueckeTrigger(false)}
              isReadOnly={!!retention?.read_only}
              context={{ bruecke_id: recentBrueckeId }}
            />
          </div>
        );
      }}
    </DashboardLayout>
  );
}

// Sprint M5.35 (29.07.2026) — PowerPaket-Bundle-Hinweis fuer Free-User im Cockpit.
// Sichtbar nur, wenn `/api/powerpaket/status.fenster_active === true`.
function FreePowerPaketHint() {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  useEffect(() => {
    api
      .get("/powerpaket/status")
      .then((r) => setStatus(r.data))
      .catch(() => setStatus(null));
  }, []);
  if (!status?.fenster_active) return null;

  return (
    <section
      data-testid="cockpit-free-powerpaket-hint"
      className="mt-10 rounded-2xl border-2 border-amber-300 bg-gradient-to-br from-amber-50 to-orange-50 p-5 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4"
    >
      <div className="flex-shrink-0 w-12 h-12 rounded-full bg-amber-500 text-white flex items-center justify-center">
        <Sparkles className="w-5 h-5" />
      </div>
      <div className="flex-1">
        <p className="text-[11px] font-bold uppercase tracking-widest text-amber-800">
          Launch-Angebot · nur begrenzt
        </p>
        <h3 className="mt-1 text-xl font-extrabold text-slate-900">
          Hol dir das PowerPaket im Jahresplan-Paket.
        </h3>
        <p className="mt-1 text-sm text-slate-700">
          Wer sich jetzt für den Jahresplan entscheidet, bekommt das PowerPaket
          automatisch dazu — kostenfreier Bonus im Launch-Fenster.
        </p>
      </div>
      <button
        type="button"
        onClick={() => navigate("/pricing")}
        data-testid="cockpit-free-powerpaket-cta"
        className="inline-flex items-center gap-2 rounded-xl bg-amber-500 hover:bg-amber-600 text-white font-bold px-4 py-2.5 text-sm shadow whitespace-nowrap"
      >
        Zu den Plänen <ArrowRight className="w-4 h-4" />
      </button>
    </section>
  );
}


// Hotfix v14f — Schnellzugriff-Kachel im Cockpit-Hub.
function CockpitQuickTile({ to, icon, label, testid }) {
  const navigate = useNavigate();
  return (
    <button
      type="button"
      onClick={() => navigate(to)}
      data-testid={testid}
      className="group flex flex-col items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-4 hover:border-[#4563BB] hover:shadow-md transition-all"
    >
      <span aria-hidden="true" className="text-2xl">{icon}</span>
      <span className="text-xs font-semibold text-slate-700 group-hover:text-[#4563BB] text-center leading-tight">
        {label}
      </span>
    </button>
  );
}


// Sprint M5.23 (29.07.2026) — Plan-Badge mit v8-Naming.
// Sprint M5.47 Hotfix (02.08.2026) — Pionier-Differenzierung: `is_pionier`-User
// bekommen ein eigenes „Vorab-Einblick"-Badge (amber) statt „Schnupper-Plan".
function PlanBadge({ user, onUpgradeClick }) {
  const licenseTier = user?.license_tier || "free";
  const tier = user?.tier;
  const isPionier = !!user?.is_pionier;

  let label = "Schnupper-Plan";
  let tone = "slate";
  if (isPionier) { label = "Pionier"; tone = "amber"; }
  else if (licenseTier === "pro_solo_monthly") { label = "Pro Solo Monat"; tone = "blue"; }
  else if (licenseTier === "pro_solo_annual") { label = "Pro Solo Jahr"; tone = "blue"; }
  else if (licenseTier === "pro_studio" || tier === "master") { label = "Pro Solo Jahr"; tone = "blue"; }

  // Sprint M5.42 (30.07.2026) — Block A5: Sub-Badge Wording v8.
  // „PowerPaket-Lifetime aktiv" statt „PowerPaket dauerhaft".
  const hasPowerPaket = Boolean(user?.powerpaket_lifetime);

  const tones = {
    slate: "bg-slate-100 text-slate-700",
    blue: "bg-[#4563BB]/10 text-[#4563BB]",
    amber: "bg-amber-100 text-amber-800",
  };

  if (tone === "slate") {
    return (
      <button
        type="button"
        onClick={onUpgradeClick}
        className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wider ${tones[tone]} hover:ring-2 hover:ring-[#4563BB]/40 transition`}
        data-testid="cockpit-plan-badge"
      >
        <span aria-hidden="true">▸</span>
        {label}
      </button>
    );  }
  return (
    <span className="inline-flex items-center gap-1.5" data-testid="cockpit-plan-badge-wrap">
      <span
        className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wider ${tones[tone]}`}
        data-testid="cockpit-plan-badge"
      >
        {label}
      </span>
      {hasPowerPaket && (
        <span
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800"
          data-testid="cockpit-powerpaket-sub-badge"
        >
          + PowerPaket-Lifetime aktiv
        </span>
      )}
    </span>
  );
}

